<!-- eslint-disable object-curly-newline -->
<template>
  <div ref="select2">
    <select class="form-control" :id="id" :name="name" :disabled="disabled" :required="required" multiple="multiple"></select>
  </div>
</template>

<script setup>
import { defineEmits, defineExpose, defineProps, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import cookies from 'vue-cookies';
import { getBaseUrl } from '@/http-common';
import '@/utils/select2Init';

// eslint-disable-next-line prefer-destructuring
const $ = window.$;

const select2 = ref(null);

const emits = defineEmits(['update:modelValue', 'select', 'change', 'click', 'unselect', 'clear']);

const props = defineProps({
  modelValue: [String, Array],
  id: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  settings: {
    type: Object,
    default: () => { },
  },
  url: {
    type: String,
    default: '',
  },
  customDataFormat: {
    type: Function,
    default: null,
  },
  inputPlaceHolder: {
    type: String,
    default: 'Digite 1 ou mais caracteres',
  },
  minimumInput: {
    type: Number,
    default: 1,
  },
  multipleSelect: {
    type: Boolean,
    default: false,
  },
  buttonClear: {
    type: Boolean,
    default: true,
  },
  method: {
    type: String,
    default: 'GET',
  },
  filterValues: {
    type: Object,
    default: () => { },
  },
  initValues: {
    type: String,
    default: '',
  },
  isCodigoValue: {
    type: Boolean,
    default: false,
  },
  showOnlyNome: {
    type: Boolean,
    default: false,
  },
});

function formatListToSelect2(list) {
  if (props.customDataFormat != null) {
    return props.customDataFormat(list);
  }
  if (props.showOnlyNome) {
    return list.map((d) => ({ id: d.ide && !props.isCodigoValue ? d.ide : d.codigo, text: `${d.nome}` }));
  }
  return list.map((d) => ({ id: d.ide && !props.isCodigoValue ? d.ide : d.codigo, text: `${d.codigo} - ${d.nome}` }));
}

// propriedade que configura o select2 para buscar via api os dados
const ajax = {
  url: `${getBaseUrl()}${props.url}`,
  cache: true,
  method: props.method,
  dataType: 'json',
  delay: 500,
  headers: {
    'Content-type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    Authorization: cookies.get('PublicToken'),
  },
  data(params) {
    return JSON.stringify({
      skip: 0,
      take: 10,
      filterDescricao: params.term,
      ...props.filterValues,
    });
  },
  processResults(data) {
    return {
      results: formatListToSelect2(data.data),
    };
  },
};

// método para selecionar o valor selecionado do modelValue
function setValue(val) {
  if (val instanceof Array) {
    $(select2.value).val([...val]);
  } else {
    $(select2.value).val([val]);
  }
  $(select2.value).trigger('change');
}

// método para configurar o select2
function startSelect2(dataValue) {
  return $(select2.value)
    .find('select')
    .select2({
      placeholder: props.placeholder,
      minimumInputLength: props.minimumInput,
      multiple: props.multipleSelect,
      allowClear: props.buttonClear,
      data: dataValue,
      ...props.settings,
      ajax,
      language: {
        inputTooShort: () => props.inputPlaceHolder,
        searching: () => 'pesquisando...',
        noResults: () => 'sem resultados encontrado.',
      },
    })
    .on('select2:select', (ev) => {
      emits('update:modelValue', $(select2.value).val() === null ? props.initValues : ev.params.data.id);
      emits('select', ev.params.data);
      emits('unselect');
    })
    .on('select', () => {
      emits('input', $(select2.value).val());
    })
    .on('select2:open', () => {
      document.querySelector('.select2-search__field').focus();
    })
    .on('select2:clear', () => {
      emits('update:modelValue', props.initValues);
      emits('clear');
    })
    .on('select2:change', () => {
    });
}

// ao criar o select2, passará nesse método e configurará o select
onMounted(() => {
  select2.value = startSelect2();
  setValue(props.modelValue);
});

// método para carregar um select2 pré selecionado
// transforma os dados padrão em um array, e depois reconfigura o select2
function selectValuesDefault(valueSelect, textValue) {
  if (valueSelect && textValue) {
    const data = [{ id: valueSelect, text: textValue }];
    if (select2.value?.prevObject) {
      select2.value = select2.value?.prevObject[0];
    }
    select2.value = startSelect2(data);
  }
  setValue(props.modelValue);
}

function selectCodigoNomeValuesDefault(valueSelect, codigoValue, textValue) {
  if (valueSelect && codigoValue && textValue) {
    const data = [{ id: valueSelect, text: `${codigoValue} - ${textValue}` }];
    if (select2.value?.prevObject) {
      select2.value = select2.value?.prevObject[0];
    }
    select2.value = startSelect2(data);
  }
  setValue(props.modelValue);
}

function clearForm() {
  select2.value = startSelect2();
  setValue(props.initValues);
}

function clearComponents() {
  if (select2.value?.select2 != null) {
    select2.value?.select2('destroy');
    select2.value = null;
  }
  if ((select2.value instanceof HTMLDivElement)) {
    select2.value = startSelect2();
    select2.value = null;
  }
}

watch(
  () => props.modelValue,
  () => setValue(props.modelValue),
  { deep: true },
);

onBeforeUnmount(() => {
  clearComponents();
});

defineExpose({
  props,
  select2,
  setValue,
  emits,
  selectValuesDefault,
  selectCodigoNomeValuesDefault,
  clearForm,
  clearComponents,
});
</script>

<style>
.select2-container--default .select2-selection--single {
  border: 1px solid #d8d6de;
}

.select2-selection__arrow {
  display: none;
}

.dark-layout .select2-container--default .select2-selection--single .select2-selection__rendered {
  color: #b4b7bd;
}
</style>
